// User-management domain types. These mirror the server's user/permission model
// (UserController + UserManagementService): a user holds access GRANTS on resources
// in the Customer -> Project -> Folder -> Contract hierarchy, each carrying a level.
// The UI calls customers "clients" (see the Client type); the server resource is
// `/api/companies` and the grant resourceType is CUSTOMER.

/** Per-resource access level. Mirrors server enum AccessLevel. */
export type AccessLevel = 'ADMIN' | 'EDITOR' | 'READER';

/**
 * Global account role. Mirrors server enum UserRole. ADMIN is a cross-tenant global admin.
 * SYSTEMADMIN is a break-glass superuser that bypasses all checks; it is DB-only and can never
 * be assigned through any UI/API surface, so it appears here for display only (no role picker).
 */
export type UserRole = 'SYSTEMADMIN' | 'ADMIN' | 'USER';

/**
 * True if a global role grants cross-tenant admin: ADMIN (platform admin) or the DB-only
 * break-glass SYSTEMADMIN. Mirrors the server's UserRole.isGlobalAdmin(). Accepts the loosely
 * typed AuthUser.role (string | null) as well as a UserRole, so it can be called directly on
 * `currentUser?.role`. Use this instead of `role === 'ADMIN'`, which silently excludes SYSTEMADMIN.
 */
export function isGlobalAdmin(role: UserRole | string | null | undefined): boolean {
  return role === 'ADMIN' || role === 'SYSTEMADMIN';
}

/** Account status. Mirrors server enum UserStatus. DEACTIVATED users are excluded from lists. */
export type UserStatus = 'ACTIVE' | 'DEACTIVATED';

/** A user as returned by the management endpoints (UserDTO). */
export interface ManagedUser {
  uuid: string;
  email: string;
  givenName: string | null;
  familyName: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt?: string;
  updatedAt?: string;
}

/** The kinds of resource a grant can apply to (GrantDTO.ResourceType). */
export type GrantResourceType = 'CUSTOMER' | 'PROJECT' | 'FOLDER' | 'CONTRACT';

/** A single access grant held by a user on a resource (GrantDTO). */
export interface AccessGrant {
  resourceType: GrantResourceType;
  resourceUuid: string;
  level: AccessLevel;
}

/** All grants held by a user across the hierarchy (UserAccessDTO). */
export interface UserAccess {
  userUuid: string;
  grants: AccessGrant[];
}

/**
 * Body for provisioning a user into a client (CreateUserRequest). If the email
 * already exists the server reuses that user and only upserts the client grant;
 * `level` defaults to READER server-side when omitted.
 */
export interface ProvisionUserPayload {
  email: string;
  givenName?: string;
  familyName?: string;
  level?: AccessLevel;
}
